import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Box, Typography, CircularProgress, Tooltip, useMediaQuery } from '@mui/material';
import { NotificationsActive } from '@mui/icons-material';
import { Button, Form, Input, InputNumber, Modal, Radio, Segmented, Select, Table, message } from 'antd';

import { responsiveSpacing } from '../../themes/responsive';
import { useAuth } from '../../context/AuthContext';
import { fetchClientsList, fetchMachinesActivity, submitClientSectorProposal } from '../../data/storesApi';
import { fetchCostRules, createCostRule, updateCostRule } from '../../data/costRulesApi';
import { MachinesActivityTable, formatActivityDate } from './MachinesActivityTable';

// A client's canon is a cost rule of concept 'canon' limited to that client (storeGroups).
const CANON = 'canon';
const isCanonOf = (rule, client) => rule.component === CANON && rule.editable && (rule.storeGroups || []).includes(client);
const canonText = (rule, t) => {
  const value = rule.method === 'per_unit'
    ? `${rule.value.toFixed(4).replace(/0{1,2}$/, '')} €`
    : `${Math.round(rule.value * 10000) / 100} %`;
  const since = t('costCanonSince', { value, date: rule.validFrom });
  return rule.validTo ? `${since} · ${t('costCanonUntil', { date: rule.validTo })}` : since;
};

// Mounted fresh on every opening, so it never shows a previous input.
const CanonModal = ({ client, rule, earliestStart, saving, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const initialMethod = rule?.method ?? 'pct_price';
  const method = Form.useWatch('method', form) ?? initialMethod;
  return (
    <Modal
      open
      title={t('costCanonTitle', { client })}
      okText={t('save')}
      cancelText={t('cancel')}
      confirmLoading={saving}
      onOk={() => form.validateFields().then(onSubmit).catch(() => {})}
      onCancel={onCancel}
    >
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('costCanonHelp')}</Typography>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          method: initialMethod,
          value: rule ? (rule.method === 'per_unit' ? rule.value : Math.round(rule.value * 10000) / 100) : undefined,
          validFrom: rule && rule.validFrom >= earliestStart ? rule.validFrom : earliestStart,
          validTo: rule?.validTo || '',
        }}
      >
        <Form.Item name="method" label={t('costRuleMethod')}>
          <Radio.Group>
            <Radio value="pct_price">{t('costRuleMethodPctPrice')}</Radio>
            <Radio value="per_unit">{t('costRuleMethodPerUnit')}</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="value" label={t('costRuleValue')} rules={[{ required: true, message: t('costRuleRequired') }]}>
          <InputNumber min={0} max={method === 'per_unit' ? undefined : 100} step={0.01} addonAfter={method === 'per_unit' ? '€' : '%'} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="validFrom" label={t('costRuleValidFrom')} rules={[{ required: true, message: t('costRuleRequired') }]}>
          <Input type="date" min={earliestStart} />
        </Form.Item>
        <Form.Item name="validTo" label={t('costRuleValidTo')} extra={t('costRuleValidToHelp')}>
          <Input type="date" min={earliestStart} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const defaultOptions = {
  sectors: [],
};

// The adapter files a value-less row as a "someone still has to pick a sector for this machine"
// marker — NULL now, the literal 'New Machine' on rows filed before that changed. It proposes
// nothing, so it must never stand in for the sector we already coded, nor ring the bell; only a
// real human proposal does either.
const SECTOR_MARKER = 'New Machine';
const getPendingSector = (record) => {
  const sector = record.pendingProposal?.sector;
  return sector == null || sector === SECTOR_MARKER ? undefined : sector;
};
const getDisplayedSector = (record) => getPendingSector(record) ?? record.sector;

export const SettingsClientsTab = () => {
  const { t, i18n } = useTranslation();
  const { currentUser } = useAuth();
  const isSmallScreen = useMediaQuery('(max-width:600px)');

  const [clientsData, setClientsData] = React.useState([]);
  const [options, setOptions] = React.useState(defaultOptions);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [submittingStores, setSubmittingStores] = React.useState({});
  const [costData, setCostData] = React.useState({ rules: [], earliestStart: null });
  const [canonModal, setCanonModal] = React.useState(null);   // { client, rule } while open
  const [savingCanon, setSavingCanon] = React.useState(false);
  const [machinesActivity, setMachinesActivity] = React.useState({ computedAt: null, machines: [] });
  // A link from another page (an inactive machine in Experimentos) opens the machines view on that machine
  const linkedMachine = useLocation().state?.machineSearch;
  const [view, setView] = React.useState(linkedMachine ? 'machines' : 'clients');
  const [machineFilter, setMachineFilter] = React.useState(linkedMachine ? 'all' : 'active');
  const [machineSearch, setMachineSearch] = React.useState(linkedMachine || '');

  const refreshCanons = React.useCallback(async () => {
    if (!currentUser?.customer_id) return;
    setCostData(await fetchCostRules(currentUser.customer_id));
  }, [currentUser]);

  const refreshClients = React.useCallback(async () => {
    if (!currentUser?.customer_id) return;
    const response = await fetchClientsList(currentUser.customer_id);
    setClientsData(response.clients || []);
    setOptions({
      sectors: response.options?.sectors || [],
    });
  }, [currentUser]);

  // Machine activity is extra: if it fails, the clients table and canons still load
  const refreshMachines = React.useCallback(async () => {
    if (!currentUser?.customer_id) return;
    try {
      setMachinesActivity(await fetchMachinesActivity(currentUser.customer_id));
    } catch (err) {
      setMachinesActivity({ computedAt: null, machines: [], failed: true });
    }
  }, [currentUser]);

  const loadClients = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([refreshClients(), refreshCanons(), refreshMachines()]);
    } catch (err) {
      console.error('Error loading clients data:', err);
      setError(t('loadClientsError'));
    } finally {
      setLoading(false);
    }
  }, [refreshClients, refreshCanons, refreshMachines, t]);

  React.useEffect(() => {
    loadClients();
  }, [loadClients]);

  const machinesByGroup = React.useMemo(() => {
    const byGroup = new Map();
    for (const machine of machinesActivity.machines) {
      if (!byGroup.has(machine.group)) byGroup.set(machine.group, []);
      byGroup.get(machine.group).push(machine);
    }
    return byGroup;
  }, [machinesActivity]);

  const machineTotals = React.useMemo(() => ({
    total: machinesActivity.machines.length,
    active: machinesActivity.machines.filter((m) => m.active === true).length,
    inactive: machinesActivity.machines.filter((m) => m.active === false).length,
  }), [machinesActivity]);

  const visibleMachines = React.useMemo(() => {
    const needle = machineSearch.trim().toLowerCase();
    return machinesActivity.machines.filter((m) => {
      if (machineFilter === 'active' && m.active !== true) return false;
      if (machineFilter === 'inactive' && m.active !== false) return false;
      return !needle || [m.machineId, m.alias, m.group, m.location, m.city]
        .some((value) => (value || '').toLowerCase().includes(needle));
    });
  }, [machinesActivity, machineFilter, machineSearch]);

  // The client's latest canon still in force or to come (a changed canon starts a new version).
  const canonOf = React.useCallback((client) => costData.rules
    .filter((r) => isCanonOf(r, client))
    .sort((a, b) => b.validFrom.localeCompare(a.validFrom))[0] ?? null, [costData]);

  const submitCanon = async (values) => {
    const { client, rule } = canonModal;
    const payload = {
      component: CANON,
      method: values.method,
      value: values.method === 'per_unit' ? values.value : values.value / 100,
      storeGroups: [client],
      validFrom: values.validFrom,
      validTo: values.validTo || null,
    };
    setSavingCanon(true);
    try {
      if (rule) await updateCostRule(currentUser.customer_id, rule.id, payload);
      else await createCostRule(currentUser.customer_id, payload);
      message.success(t('costCanonSaved'));
      setCanonModal(null);
      await refreshCanons();
    } catch (err) {
      const body = err?.response?.data;
      message.error(body?.code === 'overlap' ? t('costRuleOverlapError') : body?.message || t('costRuleSaveError'));
    } finally {
      setSavingCanon(false);
    }
  };

  const updateLocalPending = React.useCallback((storeId, value) => {
    setClientsData((prev) => prev.map((client) => {
      if (client.storeId !== storeId) return client;
      return {
        ...client,
        hasPendingProposal: true,
        pendingProposal: {
          id: client.pendingProposal?.id ?? null,
          sector: value,
          proposedBy: client.pendingProposal?.proposedBy || currentUser?.email || null,
          updatedAt: new Date().toISOString(),
        },
      };
    }));
  }, [currentUser]);

  const handleSectorChange = React.useCallback(async (record, value) => {
    if (!currentUser?.customer_id) {
      message.error(t('proposalError'));
      return;
    }

    const currentValue = getDisplayedSector(record);
    if (value === currentValue) {
      return;
    }

    const key = String(record.storeId);
    setSubmittingStores((prev) => ({ ...prev, [key]: true }));
    updateLocalPending(record.storeId, value);

    try {
      await submitClientSectorProposal(currentUser.customer_id, record.storeId, { sector: value });
      message.success(t('proposalSuccess'));
      await refreshClients();
    } catch (submitError) {
      const apiMessage = submitError?.response?.data?.message;
      message.error(apiMessage || t('proposalError'));
      await refreshClients();
    } finally {
      setSubmittingStores((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  }, [currentUser, updateLocalPending, refreshClients, t]);

  const renderSectorCell = React.useCallback((value, record) => {
    const pendingValue = getPendingSector(record);
    const displayedValue = getDisplayedSector(record);
    const key = String(record.storeId);
    const isSubmitting = submittingStores[key];
    const showPending = pendingValue && pendingValue !== record.sector;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Select
          size="small"
          style={{ minWidth: 120 }}
          value={displayedValue ?? undefined}
          onChange={(newValue) => handleSectorChange(record, newValue)}
          options={options.sectors.map((sector) => ({ label: sector, value: sector }))}
          placeholder={t('selectNewSector')}
          disabled={isSubmitting || options.sectors.length === 0}
        />
        {showPending && (
          <Tooltip title={t('proposalPendingTooltip', { value: pendingValue })} placement="top">
            <NotificationsActive fontSize="small" color="warning" />
          </Tooltip>
        )}
      </Box>
    );
  }, [handleSectorChange, options.sectors, submittingStores, t]);

  const columns = React.useMemo(() => ([
    {
      title: t('group'),
      dataIndex: 'group',
      key: 'group',
      sorter: (a, b) => (a.group || '').localeCompare(b.group || ''),
      ellipsis: true,
    },
    {
      title: t('sector'),
      dataIndex: 'sector',
      key: 'sector',
      sorter: (a, b) => (a.sector || '').localeCompare(b.sector || ''),
      render: renderSectorCell,
    },
    {
      title: t('clientsActiveMachinesColumn'),
      key: 'activeMachines',
      sorter: (a, b) => (machinesByGroup.get(a.group) || []).filter((m) => m.active).length
        - (machinesByGroup.get(b.group) || []).filter((m) => m.active).length,
      render: (_value, record) => {
        const machines = machinesByGroup.get(record.group) || [];
        return t('clientsActiveMachinesOfTotal', { active: machines.filter((m) => m.active).length, total: machines.length });
      },
    },
    {
      title: t('costCanonColumn'),
      key: 'canon',
      render: (_value, record) => {
        const rule = canonOf(record.group);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="body2" color={rule ? 'text.primary' : 'text.secondary'}>
              {rule ? canonText(rule, t) : t('costCanonNone')}
            </Typography>
            <Button size="small" onClick={() => setCanonModal({ client: record.group, rule })}>
              {rule ? t('costRuleEdit') : t('costCanonAdd')}
            </Button>
          </Box>
        );
      },
    },
  ]), [t, renderSectorCell, canonOf, machinesByGroup]);

  if (error) {
    return (
      <Box sx={{ ...responsiveSpacing.pagePadding }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('masterClients')}
        </Typography>
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...responsiveSpacing.pagePadding }}>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('masterClients')}
      </Typography>

      <Typography variant="body1" color="text.secondary">
        {t('completeClientsList')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2, visibility: loading ? 'hidden' : 'visible' }}>
        {machinesActivity.failed ? t('machinesActivityUnavailable') : t('machinesCountsLine', machineTotals)}
      </Typography>

      <Segmented
        value={view}
        onChange={setView}
        options={[
          { label: t('clientsViewClients'), value: 'clients' },
          { label: t('clientsViewMachines'), value: 'machines' },
        ]}
        style={{ marginBottom: 16 }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : view === 'machines' ? (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
            <Radio.Group value={machineFilter} onChange={(e) => setMachineFilter(e.target.value)} optionType="button" size="small">
              <Radio.Button value="active">{t('machineFilterActive')}</Radio.Button>
              <Radio.Button value="inactive">{t('machineFilterInactive')}</Radio.Button>
              <Radio.Button value="all">{t('machineFilterAll')}</Radio.Button>
            </Radio.Group>
            <Input.Search
              allowClear
              size="small"
              placeholder={t('machinesSearchPlaceholder')}
              value={machineSearch}
              onChange={(e) => setMachineSearch(e.target.value)}
              style={{ maxWidth: 320 }}
            />
          </Box>
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
            {t('machinesActivityRule')}
            {machinesActivity.computedAt && ` ${t('machinesActivityUpdatedAt', { date: formatActivityDate(machinesActivity.computedAt, i18n.language) })}`}
          </Typography>
          <MachinesActivityTable machines={visibleMachines} showGroup paginate />
        </Box>
      ) : (
        <Table
          expandable={{
            expandedRowRender: (record) => <MachinesActivityTable machines={machinesByGroup.get(record.group) || []} />,
            rowExpandable: (record) => (machinesByGroup.get(record.group) || []).length > 0,
          }}
          columns={columns}
          dataSource={clientsData}
          rowKey={(record) => record.storeId}
          pagination={{
            pageSize: 50,
            simple: isSmallScreen,
            showSizeChanger: !isSmallScreen,
            showQuickJumper: !isSmallScreen,
            showTotal: (total, range) => t('clientsPaginationTotal', {
              start: range[0],
              end: range[1],
              total,
            }),
          }}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      )}

      {canonModal && costData.earliestStart && (
        <CanonModal
          client={canonModal.client}
          rule={canonModal.rule}
          earliestStart={costData.earliestStart}
          saving={savingCanon}
          onSubmit={submitCanon}
          onCancel={() => setCanonModal(null)}
        />
      )}
    </Box>
  );
};
